
import Vue from 'vue';
import { Podcast } from '@root/modules/podcast/types/podcast';
import ContentImage from '@core/components/UI/ContentImage.vue';
import SharePopup from '@root/modules/podcast/components/fragments/SharePopup.vue';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import { CustomerData } from '@root/modules/customer/types/customer';
import getDurationString from '@root/modules/podcast/utils/getDurationString';
import { AudioItem } from '@root/modules/podcast/types/player';
import isMobileView from '@root/common/utils/isMobileView';
import getCoreMediaData from '@root/modules/podcast/utils/getCoreMediaData';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { getListenedDuration } from '@root/modules/podcast/utils/customerData';

interface ShNavigator extends Navigator {
  share: (data?: ShareData) => Promise<void>;
}

interface ShWindow extends Window {
  [index: string]: any;
  navigator: ShNavigator;
}

declare let window: ShWindow;

interface Props {
  episode: Podcast;
  isBook: boolean;
}

interface Methods {
  getEpisode: () => void;
  getListenedDuration: () => void;
  togglePlay: () => void;
  share: () => Promise<void>;
  addToPlaylist: () => void;
  removeFromPlaylist: () => void;
  getDurationString: typeof getDurationString;
}

interface Computed {
  cropperData: PictureSrcset;
  currentlyPlaying: AudioItem | null;
  customerToken: string | null;
  hasChannelAccess: boolean;
  playlistData: CustomerData[];
  isPlaying: boolean;
}

interface Data {
  item: Podcast | null;
  showSharePopup: boolean;
}

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

const cropperData: PictureSrcset = { lg: { w: 320, h: 320 }, sm: { w: 320, h: 320 }, xs: { w: 160, h: 160 } };
const bookCropperData: PictureSrcset = { lg: { w: 140, h: 210 }, sm: { w: 140, h: 210 }, xs: { w: 70, h: 105 } };

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ContentImage,
    LinkHandler,
    SharePopup,
  },
  props: {
    episode: {
      type: Object,
      required: true,
    },
    isBook: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      item: null,
      showSharePopup: false,
    };
  },
  computed: {
    cropperData() {
      return this.isBook ? bookCropperData : cropperData;
    },
    currentlyPlaying() {
      if (typeof this.$player === 'undefined') {
        return null;
      }

      return this.$player?.state?.currentlyPlaying;
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
    hasChannelAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    playlistData() {
      return this.$store.state.podcast.playlistData;
    },
    isPlaying() {
      if (!this.$player) {
        return false;
      }

      return this.$player?.state?.isPlaying && this.$player?.state?.currentlyPlaying?.articleId === this.item?.id;
    },
  },
  watch: {
    customerToken() {
      if (this.customerToken) {
        this.getListenedDuration();
      }
    },
  },
  methods: {
    async getEpisode() {
      if (!this.episode) {
        return;
      }

      let episode = this.episode;

      // Get audio duration
      const mediaData = await getCoreMediaData([episode], null, false);
      if (mediaData?.length) {
        const mediaAsset = mediaData[0];
        episode = { ...episode, duration: mediaAsset?.assetDuration || null };
      }

      this.item = episode;
    },
    async getListenedDuration() {
      if (!this.item || !this.customerToken) {
        return;
      }

      const id = String(this.episode.id);
      const listenedDurations = await getListenedDuration([id], this.customerToken);
      if (listenedDurations?.length) {
        const listenedDuration = Number(listenedDurations.find((item) => item.key === `podcast-listened-duration--${id}`)?.value) || null;
        this.item = { ...this.item, listenedDuration };
      }
    },
    async togglePlay() {
      const episode = this.item;
      if (!episode) {
        return;
      }

      // Do not proceed if player plugin is undefined
      if (typeof this.$player === 'undefined') {
        return;
      }

      // Pause if item is currently playing
      if (this.isPlaying) {
        this.$player.pause();

        return;
      }

      if (episode.content.paywall.enabled && !this.hasChannelAccess) {
        const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
        const url = buildPodcastUrl({ article: episode, domain: 'www.delfi.lt/fone', fakeDomainEnabled });

        location.href = url;
        return;
      }

      await this.$player?.setup(episode);
    },
    async share() {
      if (!isMobileView()) {
        this.showSharePopup = true;
        return;
      }

      const episode = this.item;
      if (!episode) {
        return;
      }

      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      const title = episode.content.title.text;
      const text = '';
      const url = buildPodcastUrl({ article: episode, domain: 'www.delfi.lt/fone', fakeDomainEnabled });

      if (window.navigator.share) {
        await window.navigator.share({
          title,
          text,
          url,
        });
      } else if (window.AndroidWebAppMobileAPI && window.AndroidWebAppMobileAPI.sharePage) {
        window.AndroidWebAppMobileAPI.sharePage(url, title, text);
      }
    },
    async addToPlaylist() {
      if (!this.customerToken) {
        this.$store.dispatch('piano/showLoginModal', { screen: 'login', callback: () => setTimeout(() => this.addToPlaylist(), 500) });

        return;
      }

      const episode = this.item;
      if (!episode) {
        return;
      }

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/addToPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'success',
        title: this.$t('podcast.alerts.addToPlaylist') as string,
        link: { href: 'https://www.delfi.lt/fone/mano-grojarastis', text: 'Mano grojaraštis' },
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-add-to-playlist',
        },
      });
    },
    async removeFromPlaylist() {
      const episode = this.item;
      if (!episode) {
        return;
      }

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/removeFromPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'info',
        title: this.$t('podcast.alerts.removeFromPlaylist') as string,
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-remove-from-playlist',
        },
      });
    },
    getDurationString,
  },
  async mounted() {
    await this.getEpisode();

    if (this.item && this.customerToken) {
      this.getListenedDuration();
    }
  },
});
